import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocket, useSocketEvent } from '../context/SocketContext';
import StatusSelect from '../components/StatusSelect';
import { StatusBadge } from '../components/Badges';
import ConfirmDialog from '../components/ConfirmDialog';
import PatientDetailModal from '../components/PatientDetailModal';
import NowServingDisplay from '../components/NowServingDisplay';
import { RefreshCw, Search, Inbox, Wifi, WifiOff, Clock, History as HistoryIcon, Trash2 } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';
import QRDisplay from '../components/QRDisplay';
import { todayISO as getTodayISO } from '../utils/clinicDate';

// ── Rescheduled detail badge — click to show/hide details ────────────────────
// Renders the doctor's selected "Treatment Done" entries as a short comma
// list — diagnosis entries can be legacy plain strings or objects with a name.
function treatmentNames(diagnosis) {
  if (!Array.isArray(diagnosis) || diagnosis.length === 0) return '';
  return diagnosis.map(d => (typeof d === 'string' ? d : d?.name)).filter(Boolean).join(', ');
}

function RescheduledBadge({ rescheduledFrom, rescheduledDate, rescheduledTo, rescheduledAt, newSlot, newDate }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const [pos, setPos]   = React.useState({ top:0, left:0 });
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      let left = r.left;
      if (left + 280 > window.innerWidth - 10) left = window.innerWidth - 290;
      setPos({ top: r.bottom + 8, left: Math.max(left, 8) });
    }
    setOpen(o => !o);
  }

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100,
          background: 'var(--color-warning-soft)',
          color: 'var(--color-warning)',
          border: '1px solid var(--color-warning)',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
        ⟳ Rescheduled
      </button>
      {open && ReactDOM.createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position:'fixed', inset:0, zIndex:1099 }} />
          <div style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 1100,
            width: 280,
            background: 'var(--color-surface)',
            border: '2px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
          }}>
            {/* Header bar */}
            <div style={{ background: 'var(--color-warning)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>⟳ Reschedule Details</span>
            </div>

            <div style={{ padding: 14, display: 'grid', gap: 10 }}>
              {/* Original — red tint */}
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--color-danger-soft)',
                border: '1px solid var(--color-danger)',
              }}>
                <div style={{ fontSize: 10, color: 'var(--color-danger)', fontWeight: 800, marginBottom: 4, letterSpacing: '0.06em' }}>ORIGINAL (CANCELLED)</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-ink)' }}>{rescheduledFrom || '—'}</div>
                <div style={{ color: 'var(--color-ink-soft)', fontSize: 11, marginTop: 2 }}>Date: {rescheduledDate || '—'}</div>
              </div>

              {/* New slot — green tint */}
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'var(--color-success-soft)',
                border: '1px solid var(--color-success)',
              }}>
                <div style={{ fontSize: 10, color: 'var(--color-success)', fontWeight: 800, marginBottom: 4, letterSpacing: '0.06em' }}>NEW SLOT</div>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-primary)' }}>{newSlot || rescheduledTo || '—'}</div>
                <div style={{ color: 'var(--color-ink-soft)', fontSize: 11, marginTop: 2 }}>Date: {newDate || '—'}</div>
              </div>

              {rescheduledAt && (
                <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', paddingTop: 2 }}>
                  Rescheduled at: {fmt(rescheduledAt)}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

export default function QueuePage() {
  const [patients, setPatients] = useState([]);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // patient to reschedule
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('queue_viewMode') || 'list');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  // "View History" — jumps to Patient Tracker with this patient's history
  // already loaded, instead of making staff go there and re-type the ID.
  function viewHistory(p) {
    navigate(`/${user?.role}/tracker?uid=${encodeURIComponent(p.patientUID)}`);
  }
  const { connected } = useSocket();

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get('/patients/queue');
      setPatients(res.data.patients);
    } catch (err) {
      showToast('Could not load the queue.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadQueue();
    // Safety-net poll in case the real-time socket connection ever drops
    // silently (e.g. a flaky network) - real-time events below handle the
    // normal case instantly, this is just a backup so the screen never
    // goes stale for more than a minute.
    const interval = setInterval(loadQueue, 60000);
    return () => clearInterval(interval);
  }, [loadQueue]);

  // Real-time: any other tab/device (doctor or receptionist) creating a
  // token, changing a status, or archiving a patient pushes an instant
  // update here - no manual refresh needed.
  useSocketEvent('queue:updated', () => loadQueue());

  // When someone (possibly in another tab) resets the daily queue, reload
  // immediately and let this user know what just happened, since their
  // screen is about to visibly empty out.
  useSocketEvent('queue:reset', (payload) => {
    showToast(
      `The daily queue was just reset (${payload?.modifiedCount ?? 0} token${payload?.modifiedCount === 1 ? '' : 's'} archived). Past patient records are still available under "All Patients".`,
      'info'
    );
    loadQueue();
  });

  function handlePatientUpdated(updated) {
    setPatients((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
  }

  // Delete (archive) — only offered for today's cancelled or rescheduled
  // patients, as a way to clear clutter out of the active queue view. The
  // record itself isn't destroyed — it's soft-deleted (isArchived: true)
  // and stays fully accessible under All Patients → Archived.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  async function confirmDeletePatient() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/patients/${deleteTarget._id}`);
      setPatients((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      showToast('Removed from the queue. Record is preserved under All Patients → Archived.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete this patient.', 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function handleStatusChange(id, newStatus) {
    try {
      const res = await api.put('/patients/' + id, { status: newStatus });
      handlePatientUpdated(res.data.patient);
      showToast(newStatus === 'in-progress' ? 'Patient checked in' : 'Patient checked out', 'success');
    } catch { showToast('Could not update status', 'error'); }
  }



  const filtered = patients.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.patientUID.toLowerCase().includes(q) ||
      (p.phone || '').includes(q)
    );
  });

  return (
    <AppLayout title="Today's Queue" subtitle={`${patients.length} patient${patients.length !== 1 ? 's' : ''} in today's queue`}>
      {/* ── Receptionist stats bar ─────────────────────────────── */}
      {user?.role === 'receptionist' && (
        <ReceptionistStatsBar patients={patients} />
      )}

      <div style={{ marginBottom: 20 }}>
        <NowServingDisplay showNextButton onAdvanced={() => loadQueue()} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 260px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Search by name, ID, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div
          title={connected ? 'Live updates connected' : 'Live updates disconnected — reconnecting...'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: connected ? 'var(--color-success)' : 'var(--color-warning)',
            padding: '0 4px',
          }}
        >
          {connected ? <Wifi size={15} /> : <WifiOff size={15} />}
          {connected ? 'Live' : 'Reconnecting'}
        </div>
        <QRDisplay />
        {/* View mode switcher */}
        <div style={{ display:'flex', border:'1px solid var(--color-border)', borderRadius:8, overflow:'hidden' }}>
          {[
            { key:'list',    icon:'☰',  title:'List view' },
            { key:'compact', icon:'⋮⋮', title:'Compact view' },
            { key:'card',    icon:'⊞',  title:'Card grid view' },
          ].map(v => (
            <button key={v.key} type="button" title={v.title}
              onClick={() => { setViewMode(v.key); localStorage.setItem('queue_viewMode', v.key); }}
              className={`btn btn-sm ${viewMode===v.key?'btn-primary':'btn-ghost'}`}
              style={{ borderRadius:0, padding:'6px 10px', border:'none' }}>
              {v.icon}
            </button>
          ))}
        </div>
        <button className="btn btn-outline" onClick={loadQueue}>
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 88, borderRadius: 10 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Inbox size={36} style={{ marginBottom: 12, opacity: 0.5 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>No patients in today's queue yet.</p>
          <p style={{ margin: '4px 0 0', fontSize: 13 }}>
            {user?.role === 'receptionist' ? 'Create a new token to get started.' : 'Waiting for the receptionist to add patients.'}
          </p>
        </div>
      ) : viewMode === 'compact' ? (
        /* ── Compact view ─────────────────────────────────────────────── */
        <div className="card" style={{ overflow:'hidden' }}>
          {filtered.map((p, i) => (
            <div key={p._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 14px', borderTop: i===0?'none':'1px solid var(--color-border)', gap:10, flexWrap:'wrap', background: i%2===0?'transparent':'var(--color-surface)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
                <div style={{ width:36, height:36, borderRadius:8, background:'var(--color-primary)', color:'white', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:9, opacity:0.8, lineHeight:1 }}>T</span>
                  <span style={{ fontSize:16, fontWeight:800, lineHeight:1 }}>{p.tokenNumber}</span>
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>{p.name}</div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)', display:'flex', gap:8, flexWrap:'wrap' }}>
                    {p.phone && <span>{p.phone}</span>}
                    {p.appointmentSlot && <span style={{ color:'var(--color-primary)', fontWeight:600 }}>{p.appointmentSlot}</span>}
                    {p.isRescheduled && <span style={{ color:'#d97706', fontWeight:700 }}>⟳ Rescheduled</span>}
                    <span style={{ color:'var(--color-danger)', fontWeight:600 }}>
                      Medical History: {p.medicalHistory || '-'}
                      {p.priorMedicalHistory && p.priorMedicalHistory.length > 0 && (
                        <span style={{ color:'var(--color-warning)', fontWeight:700, marginLeft:4, cursor:'help' }}
                          title={`Also on file from earlier visits:\n${p.priorMedicalHistory.join('\n')}`}>
                          (+{p.priorMedicalHistory.length} from earlier visit{p.priorMedicalHistory.length!==1?'s':''})
                        </span>
                      )}
                    </span>
                    <span style={{ color:'var(--color-success)', fontWeight:600 }}>Treatment Done: {treatmentNames(p.diagnosis) || '-'}</span>
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                <StatusBadge status={p.status} />
                {p.phone && <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" />}
                <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
                <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}
                  onClick={() => viewHistory(p)} title="View History"><HistoryIcon size={13} /></button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}
                  onClick={() => setRescheduleTarget(p)} title="Reschedule">⟳</button>
                {(p.status === 'cancelled' || p.isRescheduled) && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}
                    onClick={() => setDeleteTarget(p)} title="Delete"><Trash2 size={13} color="var(--color-danger)" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : viewMode === 'card' ? (
        /* ── Card grid view ───────────────────────────────────────────── */
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
          {filtered.map((p, i) => (
            <div key={p._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
            <div className="card tilt3d-card" style={{ padding:14, display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ width:40, height:40, borderRadius:10, background:'var(--color-primary)', color:'white', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:8, opacity:0.8 }}>TOKEN</span>
                  <span style={{ fontSize:18, fontWeight:800, lineHeight:1 }}>{p.tokenNumber}</span>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div style={{ fontWeight:700, fontSize:14, cursor:'pointer' }} onClick={() => setSelectedPatient(p)}>{p.name}</div>
              <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>
                {p.gender}{p.age?`, ${p.age}y`:''} {p.phone && `· ${p.phone}`}
              </div>
              {p.appointmentSlot && <div style={{ fontSize:11, fontWeight:600, color:'var(--color-primary)' }}>⏰ {p.appointmentSlot}</div>}
              <div style={{ fontSize:11, color:'var(--color-danger)', fontWeight:600 }}>Medical History: {p.medicalHistory || '-'}</div>
              {p.priorMedicalHistory && p.priorMedicalHistory.length > 0 && (
                <div style={{ fontSize:10, color:'var(--color-warning)', fontStyle:'italic' }}>
                  Also on file: {p.priorMedicalHistory.join(' · ')}
                </div>
              )}
              <div style={{ fontSize:11, color:'var(--color-success)', fontWeight:600 }}>Treatment Done: {treatmentNames(p.diagnosis) || '-'}</div>
              {p.isRescheduled && <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:100, background:'#fef3c7', color:'#d97706', border:'1px solid #fcd34d', width:'fit-content' }}>⟳ Rescheduled</span>}
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:'auto' }}>
                <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
                {p.phone && <WhatsAppButton patient={p} slot={p.appointmentSlot} date={p.tokenDate} tokenNumber={p.tokenNumber} size="xs" />}
                <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}
                  onClick={() => viewHistory(p)} title="View History"><HistoryIcon size={13} /> History</button>
                <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px' }}
                  onClick={() => setRescheduleTarget(p)} title="Reschedule">⟳ Reschedule</button>
                {(p.status === 'cancelled' || p.isRescheduled) && (
                  <button className="btn btn-ghost btn-sm" style={{ fontSize:11, padding:'3px 7px', color:'var(--color-danger)' }}
                    onClick={() => setDeleteTarget(p)} title="Delete"><Trash2 size={13} /> Delete</button>
                )}
              </div>
            </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Default list view ────────────────────────────────────────── */
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map((p, i) => (
            <div key={p._id} className="ticket stagger-in" style={{ '--stagger-i': i }}>
              <div className="ticket-stub">
                <div className="ticket-stub-label">Token</div>
                <div className="ticket-stub-number">{p.tokenNumber}</div>
              </div>
              <div className="ticket-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div className="ticket-name" style={{ cursor: 'pointer' }} onClick={() => setSelectedPatient(p)}>
                      {p.name}
                    </div>
                    <div className="ticket-meta">
                      <span>{p.gender}{p.age ? `, ${p.age}y` : ''}</span>
                      {p.phone && <span>· {p.phone}</span>}
                      <span className="ticket-uid">· {p.patientUID}</span>
                    </div>
                    {p.reasonForVisit && (
                      <div style={{ fontSize: 13, color: 'var(--color-ink-soft)', marginBottom: 4 }}>{p.reasonForVisit}</div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--color-danger)', fontWeight: 600, marginBottom: 4 }}>Medical History: {p.medicalHistory || '-'}</div>
                    {p.priorMedicalHistory && p.priorMedicalHistory.length > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--color-warning)', fontStyle: 'italic', marginBottom: 4 }}>
                        Also on file from earlier visits: {p.priorMedicalHistory.join(' · ')}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--color-success)', fontWeight: 600, marginBottom: 4 }}>Treatment Done: {treatmentNames(p.diagnosis) || '-'}</div>
                    {p.appointmentSlot && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <Clock size={12} />
                        {p.appointmentSlot}
                        {p.appointmentDate && p.appointmentDate !== p.tokenDate && (
                          <span style={{ fontWeight: 400, color: 'var(--color-ink-faint)' }}>· {p.appointmentDate}</span>
                        )}
                      </div>
                    )}
                    {p.isRescheduled && (
                      <RescheduledBadge rescheduledFrom={p.rescheduledFrom} rescheduledDate={p.rescheduledDate} rescheduledTo={p.rescheduledTo} rescheduledAt={p.rescheduledAt} newSlot={p.appointmentSlot} newDate={p.appointmentDate} />
                    )}
                  </div>
                </div>
                <div className="ticket-actions">

                  <StatusSelect patient={p} onUpdated={handlePatientUpdated} compact />
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelectedPatient(p)}>
                    View details
                  </button>
                  <button className="btn btn-outline btn-sm"
                    onClick={() => viewHistory(p)}
                    title="View this patient's full visit history"
                    style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <HistoryIcon size={13} /> View History
                  </button>
                  <button className="btn btn-outline btn-sm"
                    onClick={() => setRescheduleTarget(p)}
                    title="Reschedule this appointment"
                    style={{ display:'flex', alignItems:'center', gap:4 }}>
                    ⟳ Reschedule
                  </button>
                  {(p.status === 'cancelled' || p.isRescheduled) && (
                    <button className="btn btn-outline btn-sm"
                      onClick={() => setDeleteTarget(p)}
                      title="Delete this patient from the queue"
                      style={{ display:'flex', alignItems:'center', gap:4, color:'var(--color-danger)', borderColor:'var(--color-danger)' }}>
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                  {p.phone && (
                    <WhatsAppButton
                      patient={p}
                      slot={p.appointmentSlot}
                      date={p.tokenDate}
                      tokenNumber={p.tokenNumber}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Reschedule Modal ── */}
      {rescheduleTarget && (
        <RescheduleModal
          patient={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onDone={(updated) => { handlePatientUpdated(updated); setRescheduleTarget(null); }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.name}'s ${deleteTarget?.status === 'cancelled' ? 'cancelled' : 'rescheduled'} entry?`}
        message="This removes it from today's active queue. The full record is preserved and stays available under All Patients → Archived — nothing is permanently lost."
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        danger onConfirm={confirmDeletePatient} onCancel={() => setDeleteTarget(null)}
      />

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          editable={user?.role === 'doctor'}
          onClose={() => setSelectedPatient(null)}
          onUpdated={(updated) => {
            handlePatientUpdated(updated);
            setSelectedPatient(updated);
          }}
        />
      )}
    </AppLayout>
  );
}

// ── RescheduleModal ───────────────────────────────────────────────────────────
function RescheduleModal({ patient, onClose, onDone }) {
  const [patientAppts, setPatientAppts] = React.useState([]);
  const [fromAppt, setFromAppt]         = React.useState(null);
  const [newDate, setNewDate]           = React.useState('');
  const [newSlot, setNewSlot]           = React.useState('');
  const [takenSlots, setTakenSlots]     = React.useState([]);
  const [saving, setSaving]             = React.useState(false);

  React.useEffect(() => {
    if (!patient?.patientUID) return;
    const today = getTodayISO();
    api.get('/patients', { params: { patientUID: patient.patientUID, limit:50 } })
      .then(r => {
        const upcoming = (r.data.patients||[]).filter(v =>
          v.appointmentDate >= today &&
          v.status !== 'completed' &&
          v.status !== 'cancelled' &&
          v.appointmentSlot
        );
        setPatientAppts(upcoming);
      }).catch(()=>{});
  }, [patient?.patientUID]);

  React.useEffect(() => {
    if (!newDate) { setTakenSlots([]); return; }
    api.get('/patients/slots', { params:{ date: newDate } })
      .then(r => setTakenSlots((r.data.booked||[]).filter(b => b.status !== 'completed')))
      .catch(()=>{});
  }, [newDate]);

  async function handleSave() {
    if (!fromAppt) { alert('Select an appointment to reschedule.'); return; }
    if (!newDate)  { alert('Select a new date.'); return; }
    if (!newSlot)  { alert('Select or type a new slot.'); return; }
    setSaving(true);
    try {
      // Create new token for same patient with rescheduled info
      const res = await api.post('/patients', {
        name: patient.name, age: patient.age, gender: patient.gender,
        phone: patient.phone, address: patient.address,
        medicalHistory: patient.medicalHistory,
        reasonForVisit: patient.reasonForVisit || '',
        sourcePatientId: patient._id, sourcePatientUID: patient.patientUID,
        appointmentDate: newDate, appointmentSlot: newSlot,
        isRescheduled: true,
        rescheduledFrom: fromAppt.appointmentSlot,
        rescheduledDate: fromAppt.appointmentDate,
        rescheduledAt:   new Date().toISOString(),
        cancelOldSlotId: fromAppt._id,
      });
      onDone(res.data.patient);
      alert('Appointment rescheduled successfully!');
    } catch(err) {
      alert(err.response?.data?.message || 'Could not reschedule.');
    } finally { setSaving(false); }
  }

  const todayISO = getTodayISO();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth:460, padding:0, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{ background:'var(--color-primary)', padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#fff' }}>⟳ Reschedule Appointment</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.75)', marginTop:2 }}>{patient.name} · {patient.patientUID}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', borderRadius:6, padding:'5px 8px', cursor:'pointer', fontSize:16 }}>✕</button>
        </div>

        <div style={{ padding:18, display:'flex', flexDirection:'column', gap:16, maxHeight:'70vh', overflowY:'auto' }}>
          {/* Step 1 */}
          <div>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:22, height:22, borderRadius:'50%', background:'var(--color-primary)', color:'#fff', fontSize:11, fontWeight:800, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>1</span>
              Select appointment to reschedule
            </div>
            {patientAppts.length === 0 ? (
              <div style={{ fontSize:13, color:'var(--color-ink-faint)', fontStyle:'italic', padding:'8px 12px', background:'var(--color-surface)', borderRadius:8 }}>
                No upcoming booked appointments found for this patient.
              </div>
            ) : (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {patientAppts.map((appt,i) => {
                  const sel = fromAppt?._id === appt._id;
                  return (
                    <button key={i} type="button" onClick={() => { setFromAppt(sel?null:appt); setNewDate(''); setNewSlot(''); }}
                      style={{ padding:'10px 14px', borderRadius:10, cursor:'pointer', textAlign:'left', border:`2px solid ${sel?'var(--color-primary)':'var(--color-border)'}`, background: sel?'var(--color-primary)':'var(--color-card)', color: sel?'#fff':'var(--color-ink)' }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{appt.appointmentSlot}</div>
                      <div style={{ fontSize:11, opacity:0.8 }}>{appt.appointmentDate}{appt.doctorName?` · ${appt.doctorName.replace(/^dr\.?\s*/i,'').trim()?'Dr. '+appt.doctorName.replace(/^dr\.?\s*/i,'').trim():''}`:''}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 2 */}
          {fromAppt && (
            <div>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:22, height:22, borderRadius:'50%', background:'var(--color-primary)', color:'#fff', fontSize:11, fontWeight:800, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>2</span>
                New date &amp; slot
                <span style={{ fontSize:11, fontWeight:400, color:'var(--color-ink-faint)' }}>(replacing {fromAppt.appointmentSlot} on {fromAppt.appointmentDate})</span>
              </div>

              {/* Date picker */}
              <div className="field" style={{ marginBottom:10 }}>
                <label className="label">New Date *</label>
                <input className="input" type="date" value={newDate} min={todayISO}
                  onChange={e => { setNewDate(e.target.value); setNewSlot(''); }} />
              </div>

              {/* Slot grid — only when a date is picked */}
              {newDate && (
                <div>
                  <label className="label">New Slot *</label>
                  {takenSlots.length > 0 && (
                    <div style={{ marginBottom:8, fontSize:12, color:'var(--color-ink-faint)' }}>
                      Greyed = already booked on {newDate}
                    </div>
                  )}
                  {/* Common slot options — tap to select */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))', gap:6, marginBottom:10 }}>
                    {[
                      '9:00–9:30 AM','9:30–10:00 AM','10:00–10:30 AM','10:30–11:00 AM',
                      '11:00–11:30 AM','11:30–12:00 PM','12:00–12:30 PM',
                      '2:00–2:30 PM','2:30–3:00 PM','3:00–3:30 PM',
                      '3:30–4:00 PM','4:00–4:30 PM','4:30–5:00 PM',
                      '5:00–5:30 PM','5:30–6:00 PM',
                    ].map(slot => {
                      const taken = takenSlots.some(b => b.appointmentSlot === slot);
                      const selected = newSlot === slot;
                      return (
                        <button key={slot} type="button" disabled={taken}
                          onClick={() => setNewSlot(selected ? '' : slot)}
                          style={{
                            padding:'7px 4px', borderRadius:8, fontSize:11, fontWeight:700,
                            cursor: taken ? 'not-allowed' : 'pointer',
                            border: `2px solid ${selected ? 'var(--color-primary)' : taken ? 'var(--color-border)' : 'var(--color-accent)'}`,
                            background: selected ? 'var(--color-primary)' : taken ? 'var(--color-border)' : 'var(--color-accent-soft)',
                            color: selected ? '#fff' : taken ? 'var(--color-ink-faint)' : 'var(--color-accent)',
                            opacity: taken ? 0.5 : 1,
                            textAlign:'center',
                          }}>
                          {slot}
                          {taken && <div style={{ fontSize:9, fontWeight:400, marginTop:1 }}>Taken</div>}
                        </button>
                      );
                    })}
                  </div>
                  {/* Custom slot free-text */}
                  <div className="field" style={{ marginBottom:0 }}>
                    <label className="label">Or type a custom slot</label>
                    <input className="input" value={newSlot}
                      onChange={e => setNewSlot(e.target.value)}
                      placeholder="e.g. 6:00–6:30 PM" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 18px', borderTop:'1px solid var(--color-border)', display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!fromAppt||!newDate||!newSlot||saving} onClick={handleSave}>
            {saving ? 'Rescheduling…' : '✓ Confirm Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ReceptionistStatsBar ──────────────────────────────────────────────────────
function ReceptionistStatsBar({ patients }) {
  const [showBar, setShowBar] = React.useState(() => localStorage.getItem('reception_bar_visible') !== 'false');
  const [expenses, setExpenses] = React.useState([]);

  const todayStr = getTodayISO();
  const todayPatients = patients.filter(p => p.tokenDate === todayStr);

  // Today's collected revenue
  const todayRevenue = todayPatients.reduce((s, p) => s + (p.billing?.amountPaid || 0), 0);
  const todayPending = todayPatients.reduce((s, p) => {
    const total = p.billing?.amount || 0;
    return s + Math.max(total - (p.billing?.amountPaid || 0), 0);
  }, 0);

  // Expenses management (add/view/delete individual entries) now lives on
  // its own dedicated Expenses page (see sidebar) — only the day's total is
  // still shown here, as a quick-glance figure alongside revenue.
  const loadExpenses = React.useCallback(() => {
    api.get('/expenses', { params: { date: todayStr } })
      .then(res => setExpenses(res.data.expenses || []))
      .catch(() => setExpenses([]));
  }, [todayStr]);

  React.useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useSocketEvent('expense:created', loadExpenses);
  useSocketEvent('expense:deleted', loadExpenses);

  const totalExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const fmt = n => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  function toggleBar() {
    const next = !showBar;
    setShowBar(next);
    localStorage.setItem('reception_bar_visible', next);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Toggle bar */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: showBar ? 8 : 0 }}>
        <button className="btn btn-outline btn-sm" onClick={toggleBar}
          style={{ fontSize:11, padding:'3px 10px' }}>
          {showBar ? '▲ Hide Stats' : '▼ Show Stats'}
        </button>
      </div>
      {showBar && <>
      {/* Stats row */}
      <div className="stat-grid" style={{ marginBottom: 12 }}>
        {[
          { label: "Today's Revenue", value: `₹${fmt(todayRevenue)}`, color: 'var(--color-success)', note: `${todayPatients.filter(p => p.billing?.paymentStatus === 'paid').length} paid` },
          { label: "Pending Collections", value: `₹${fmt(todayPending)}`, color: todayPending > 0 ? 'var(--color-warning)' : 'var(--color-ink-faint)', note: `${todayPatients.filter(p => p.billing?.paymentStatus !== 'paid').length} unpaid` },
          { label: "Today's Expenses", value: `₹${fmt(totalExpenses)}`, color: 'var(--color-danger)', note: `${expenses.length} entries` },
          { label: 'Net Today', value: `₹${fmt(todayRevenue - totalExpenses)}`, color: 'var(--color-primary)', note: 'revenue − expenses' },
        ].map(s => (
          <div key={s.label} className="tilt3d-scene">
          <div className="card tilt3d-card" style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontWeight: 800, fontSize: 20, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', marginTop: 2 }}>{s.note}</div>
          </div>
          </div>
        ))}
      </div>

      {/* Doctor payments from today's patients */}
      {todayPatients.filter(p => p.doctorName && p.billing?.amountPaid > 0).length > 0 && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', marginBottom: 8, textTransform: 'uppercase' }}>
            Consultant Payments Today
          </div>
          {(() => {
            const docMap = {};
            todayPatients.forEach(p => {
              if (!p.doctorName) return;
              if (!docMap[p.doctorName]) docMap[p.doctorName] = { collected: 0, patients: 0 };
              docMap[p.doctorName].collected += p.billing?.amountPaid || 0;
              docMap[p.doctorName].patients++;
            });
            return Object.entries(docMap).map(([doc, d]) => (
              <div key={doc} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Dr. {doc}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>{d.patients} patient{d.patients !== 1 ? 's' : ''} today</div>
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--color-success)' }}>₹{fmt(d.collected)}</div>
              </div>
            ));
          })()}
        </div>
      )}

      {/* Expenses management (add / view individual entries / delete) now
          lives on its own page — see "Expenses" in the sidebar. Only the
          quick-glance total stays here, in the stats row above. */}
      </>}
    </div>
  );
}
